<template>
	<div class="cog-payment__field">
		<c-input v-if="!overrideInputStyles" ref="paymentStyle" style="display: none;" inputmode="numeric" placeholder="placeholder" />
		<slot :input-styles="inputStyles" :save-card="saveCard"></slot>
		<transition	name="cog-error-message">
			<div v-if="errorMessage" aria-live="assertive" class="cog-form-payment-error cog-error-message">
				{{ errorMessage }}
				<a v-if="supportPageLinkUrl" :href="supportPageLinkUrl" target="_blank" class="matins">
					{{ supportPageLinkText || 'Learn how to fix this.' }}
				</a>
			</div>
		</transition>
		<c-field v-if="saveCard" v-slot="{ field }" source="Save_Card_Agreement" hide-label class="cog-payment__save-card">
			<c-checkbox v-model="field.value" :label="$resource('card-on-file-agreement')" />
		</c-field>
	</div>
</template>

<script>
	import { getFormStyles } from './payment-form-style';
	import CInput from '../Input.vue';
	import CCheckbox from '../Checkbox.vue';
	import { FormEvents } from 'src/framework/public/events';

	export default {
		name: 'CPayment',
		components: {
			CInput,
			CCheckbox
		},
		inject: {
			$resource: {
				from: '$resource',
				required: true
			},
			formEvents: {
				from: 'formEvents',
				default: null
			}
		},
		props: {
			entry: { type: Object, required: true },
			saveCard: { type: Boolean, default: false },
			errorMessage: { type: String, default: '' },
			supportPageLinkUrl: { type: String, default: '' },
			supportPageLinkText: { type: String, default: '' },
			useThemeSettings: { type: Boolean, default: true },
			overrideInputStyles: { type: Object, default: null }
		},
		data() {
			return {
				styleReference: null,
				themeSettings: { ...this.entry.Form_Theme }
			};
		},
		computed: {
			inputStyles() {
				return this.overrideInputStyles ? this.overrideInputStyles : this.getFormStyles();
			}
		},
		mounted() {
			this.styleReference = this.getPaymentStyleElement();

			if (this.formEvents) {
				this.formEvents.on(FormEvents.ThemeUpdated, (evt) => {
					Object.assign(this.themeSettings, evt.data.themeSettings);
					const addedPaymentInputStyles = this.$el.querySelector('.payment-input-styles');
					if (addedPaymentInputStyles)
						addedPaymentInputStyles.remove();
					this.addPaymentInputStyles();
				});
			}

			if (this.styleReference)
				this.addPaymentInputStyles();
		},
		methods: {
			/**
			 * Gets a map of element styles for payment fields
			 */
			getFormStyles() {
				if (this.styleReference)
					return getFormStyles(this.styleReference, this.useThemeSettings ? this.themeSettings : {});
				return {};
			},

			/**
			 * Gets the element from which to derive styles for payment fields
			 * @returns Element
			 */
			getPaymentStyleElement() {
				if (this.$refs.paymentStyle) {
					const paymentStyleComponent = this.$refs.paymentStyle;
					if (paymentStyleComponent) {
						const paymentStyleInput = paymentStyleComponent.$el.querySelector('input');
						if (paymentStyleInput) {
							return paymentStyleInput;
						}
						else {
							console.log('DEBUG: No payment style input found');
						}
					}
				}
			},

			/**
			 * Add text input styles to Stripe/Square divs
			 */
			addPaymentInputStyles() {
				const inputStyles = this.getFormStyles();
				const nativeBorderStyles = inputStyles.computedStyles.border.includes('2px inset');

				const includedStyles = [
					'background-color',
					'background-image',
					'background-position',
					'background-repeat',
					'background-size',

					'border-top-color',
					'border-top-left-radius',
					'border-top-right-radius',
					'border-top-style',
					'border-top-width',

					'border-right-color',
					'border-right-style',
					'border-right-width',

					'border-bottom-color',
					'border-bottom-left-radius',
					'border-bottom-right-radius',
					'border-bottom-style',
					'border-bottom-width',

					'border-left-color',
					'border-left-style',
					'border-left-width',

					'box-shadow',

					'padding-top',
					'padding-right',
					'padding-bottom',
					'padding-left'
				].reduce(function(includedStyles, styleProperty) {
					let styleValue = inputStyles.computedStyles[styleProperty];

					// transform reported native border style (2px inset) to what it actually looks like (1px solid).
					if (nativeBorderStyles && styleProperty.includes('border')) {
						if (styleProperty.includes('-style'))
							styleValue = 'solid';
						else if (styleProperty.includes('-width'))
							styleValue = '1px';
					}
					includedStyles += styleProperty + ':' + styleValue + ';';
					return includedStyles;
				}, '');

				const inputStyleBlock = document.createElement('style');
				inputStyleBlock.className = 'payment-input-styles';
				inputStyleBlock.textContent = `:root:root:root:root:root .cog-payment__field .cog-payment__frame .cog-input > div{${includedStyles}}`;
				this.$el.appendChild(inputStyleBlock);
			}
		}
	};
</script>

<style lang="scss">
#{$specificity-base} {
	@include at-root(2) {

		.cog-cognito--chameleon .StripeElement--focus {
			border-color: var(--highlight);
			box-shadow: var(--input-focus__box-shadow);
		}
	}

	.cog-payment__frame .cog-input iframe {
		// Prevent spacing below iframe due to default inline display
		display: block;
	}

	.cog-input__placeholder {
		@include input-essential;
		color: var(--placeholder__color);
	}

	// Override spacing around normal fields
	.cog-field.cog-payment__save-card.cog-payment__save-card {
		width: 100%;
		margin: 0;
		transition: padding $speed-half, margin $speed-half;

		&.is-error {
			margin-top: var(--gutter);
		}
	}

	// When the checkbox is not in error it should line up with CC inputs
	.cog-field:not(.is-error).cog-payment__save-card {
		padding: 0;
	}
}
</style>
